import { useAppDispatch, useAppSelector } from '@app/store/hooks';
import CssBaseline from '@mui/material/CssBaseline';
import InitColorSchemeScript from '@mui/material/InitColorSchemeScript';
import { ThemeProvider as MuiThemeProvider, useColorScheme } from '@mui/material/styles';
import {
  COLOR_MODE_STORAGE_KEY,
  type ColorMode,
  isColorMode,
  setColorMode,
} from '@theme/colorModeSlice';
import { theme } from '@theme/theme';
import { type ReactNode, useEffect } from 'react';

interface Props {
  children: ReactNode;
}

/**
 * Pushes Redux's color mode into MUI's `useColorScheme`. Skips the write
 * when MUI is already on the right mode — important because `setMode`'s
 * identity changes whenever MUI's internal state changes, which would
 * otherwise re-run this effect and re-impose Redux's value, fighting
 * MUI's own cross-tab storage sync and causing a flip-flop loop.
 */
function ColorSchemeSync() {
  const mode = useAppSelector((s) => s.colorMode.mode);
  const { mode: muiMode, setMode } = useColorScheme();
  useEffect(() => {
    if (muiMode === mode) return;
    setMode(mode);
  }, [mode, muiMode, setMode]);
  return null;
}

/**
 * Mirrors localStorage writes from other tabs into the Redux color-mode
 * slice. Without this, two open tabs would each only know about their
 * own toggles — and combined with MUI's own cross-tab broadcast, the
 * difference between Redux and MUI in the *other* tab causes a runaway
 * "flip back and forth" loop.
 *
 * Per the storage-event spec the listener only fires in other documents,
 * never in the tab that performed the write, so we don't need to guard
 * against echoing our own change.
 */
function useColorModeCrossTabSync() {
  const dispatch = useAppDispatch();
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const onStorage = (e: StorageEvent) => {
      if (e.key !== COLOR_MODE_STORAGE_KEY) return;
      if (e.newValue === null) return;
      if (!isColorMode(e.newValue)) return;
      const next: ColorMode = e.newValue;
      dispatch(setColorMode(next));
    };
    window.addEventListener('storage', onStorage);
    return () => {
      window.removeEventListener('storage', onStorage);
    };
  }, [dispatch]);
}

export function ThemeProvider({ children }: Props) {
  const mode = useAppSelector((s) => s.colorMode.mode);
  useColorModeCrossTabSync();
  return (
    <>
      <InitColorSchemeScript
        attribute="data-mui-color-scheme"
        modeStorageKey={COLOR_MODE_STORAGE_KEY}
        defaultMode={mode}
      />
      <MuiThemeProvider
        theme={theme}
        defaultMode={mode}
        modeStorageKey={COLOR_MODE_STORAGE_KEY}
      >
        <CssBaseline enableColorScheme />
        <ColorSchemeSync />
        {children}
      </MuiThemeProvider>
    </>
  );
}
